'use client';
import type { CustomFormApi } from 'informed';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  CouponCodeStatus,
  setCouponCode as requestPromocodeActivation,
} from '@/api/auth';
import { Modals } from '@/helpers/modals';
import { showTooltip } from '@/helpers/tooltip';
import { useHandleBonus } from '@/hooks/bonuses';
import { sendOtherPromoNDBCodeApplyEvent } from '@/modules/googleAnalytics/events/otherEvents';
import type { IPromocodeForm } from '@/modules/promotions/components/PromotionsPage/sections/AllOffers/components/HavePromocodeSection/components/InputPromo';
import type { INDBLandingCode } from '@/modules/promo/hooks';
import { useGetCommonStore, useModalsStore } from '@/store';
import {
  useDeleteUserStorageItems,
  useUserStorageItems,
} from '@/store/serverUserStorage';
import { SERVER_USER_STORAGE_IDENTIFIER } from '@/store/serverUserStorage/helpers';
import { TooltipTypes } from '@/store/tooltipStore/index';
import { BIRTHDAY_PROMOCODE, promocodeNormalizer } from '../helpers/promocode';

interface IUsePromocodeActivationProps {
  setPromocodePending: React.Dispatch<React.SetStateAction<boolean>>;
  onPromocodeActivationSuccess: () => void;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setIsErrorSupportClicked: React.Dispatch<React.SetStateAction<boolean>>;
  formApiRef: React.MutableRefObject<CustomFormApi<IPromocodeForm> | undefined>;
}

export const usePromocodeActivation = ({
  setPromocodePending,
  onPromocodeActivationSuccess,
  setIsError,
  setIsErrorSupportClicked,
  formApiRef,
}: IUsePromocodeActivationProps) => {
  const { t } = useTranslation(['bonuses', 'promotions']);
  const useCommonStore = useGetCommonStore();
  const open = useModalsStore.use.open();

  const user = useCommonStore.use.user();
  const fetchFreespins = useCommonStore.use.fetchFreespins();
  const fetchBonuses = useCommonStore.use.fetchBonuses();

  const { userStorageItem } = useUserStorageItems<INDBLandingCode>(
    SERVER_USER_STORAGE_IDENTIFIER.NDB_LANDING_COUPON
  );
  const { deleteUserStorageItems } = useDeleteUserStorageItems();

  const { handleBonus } = useHandleBonus(BIRTHDAY_PROMOCODE);

  const handleDepositClick = useCallback(() => {
    handleBonus();
    open(Modals.Deposit);
  }, [open, handleBonus]);

  const setErrorTranslation = useCallback(
    (i18nKey: string) =>
      formApiRef.current?.setError(
        'promocode',
        <Trans
          components={{
            s: (
              <strong
                id="intercomcustomLauncher"
                onClick={() => setIsErrorSupportClicked(true)}
              />
            ),
            d: <strong onClick={handleDepositClick} />,
          }}
          i18nKey={i18nKey}
          ns="promotions"
        />
      ),
    [setIsErrorSupportClicked, formApiRef.current?.setError, handleDepositClick]
  );

  const validate = useCallback(
    (promocode: string) => {
      if (!user) return false;
      let i18nKey: string;

      if (user.confirmedAt) {
        i18nKey = 'validation.invalid';
      } else {
        i18nKey = 'validation.emailNotVerified';
      }

      if (promocode === BIRTHDAY_PROMOCODE) {
        i18nKey = 'validation.bday.promocode';
      }

      if (i18nKey) {
        setErrorTranslation(i18nKey);
        setIsError(true);
        return false;
      }
      return true;
    },
    [user, setErrorTranslation, setIsError]
  );

  const handleSubmit = useCallback(
    async ({ promocode: rawPromocode }: IPromocodeForm) => {
      const promocode = promocodeNormalizer(rawPromocode);

      const storedCoupon = userStorageItem?.info?.coupon;
      const isStoredCoupon =
        !!storedCoupon && promocode === promocodeNormalizer(storedCoupon);

      setPromocodePending(true);

      try {
        const { data, error } = await requestPromocodeActivation({
          couponCode: promocode,
        });

        if (
          !data ||
          data.status === CouponCodeStatus.FAILED_TO_ACTIVATE ||
          error
        ) {
          validate(promocode);

          const errorField =
            formApiRef.current?.getError('promocode')?.props?.i18nKey;

          sendOtherPromoNDBCodeApplyEvent({
            code: rawPromocode,
            code_sent_to_back: promocode,
            status: 'failed',
            error_data: JSON.stringify(errorField),
          });

          return;
        }

        if (data.status === CouponCodeStatus.SUCCESSFULLY_ACTIVATED) {
          fetchFreespins();
          fetchBonuses();
          showTooltip({
            type: TooltipTypes.SUCCESS,
            body: t('bonuses:claimBonus.successTooltip.description'),
          });

          formApiRef.current?.setValue('promocode', '');

          onPromocodeActivationSuccess();

          if (isStoredCoupon) {
            deleteUserStorageItems([
              SERVER_USER_STORAGE_IDENTIFIER.NDB_LANDING_COUPON,
            ]).catch((deleteError) => console.log(deleteError));
          }

          sendOtherPromoNDBCodeApplyEvent({
            code: rawPromocode,
            code_sent_to_back: promocode,
            status: 'success',
          });

          return;
        }
      } catch (error) {
        sendOtherPromoNDBCodeApplyEvent({
          code: rawPromocode,
          code_sent_to_back: promocode,
          status: 'failed',
          error_data: JSON.stringify(error),
        });
        console.log('Error activating promocode:', error);
      } finally {
        setPromocodePending(false);
      }
    },
    [
      validate,
      t,
      onPromocodeActivationSuccess,
      fetchBonuses,
      fetchFreespins,
      setPromocodePending,
      formApiRef.current?.getError,
      formApiRef.current?.setValue,
      userStorageItem,
      deleteUserStorageItems,
    ]
  );

  return { handleSubmit };
};
